import { useSelector } from 'react-redux';
import { breakpoints } from '@styles/index';
import PropTypes from 'prop-types';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import imageHelper from '@utils/imageHelper';
import styled from 'styled-components';
import Container, { Title } from './styles';

const BackgroundColor = styled.section`
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(229, 244, 255, 0.44) 100%
  );
  padding: 120px 0;
  @media (max-width: ${breakpoints.tablet}) {
    padding: 80px 0;
  }
  @media (max-width: ${breakpoints.mobile}) {
    padding: 40px 16px;
    margin-bottom: 40px;
  }
`;

const Block = styled.div`
  grid-column: span 24;
  align-items: center;
  justify-content: center;
  display: flex;
  margin-bottom: 32px;
  @media (max-width: ${breakpoints.tablet}) {
    justify-content: left;
  }
`;

const FullWidth = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  @media (max-width: ${breakpoints.tablet}) {
    grid-column: 1/13;
  }
`;

const Image = styled.img`
  background: #f0f4f7;
  border-radius: 8px;
  width: 80px;
  height: 80px;
  object-fit: cover;
  cursor: pointer;
  user-drag: none;
  user-select: none;
  @media (max-width: ${breakpoints.tablet}) {
    width: 56px;
    height: 56px;
  }
`;

const Icon = styled.img`
  margin: 0 16px;
  cursor: pointer;
  user-drag: none;
  user-select: none;
  object-fit: cover;
`;

const StepCover = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  column-gap: 16px;
  @media (max-width: ${breakpoints.mobile}) {
    display: block;
  }
`;

const Header = styled(Title)`
  grid-column: 7/19;
  text-align: center;
  @media (max-width: ${breakpoints.tablet}) {
    grid-column: span 24;
    text-align: left;
  }
  @media (max-width: ${breakpoints.mobile}) {
    text-align: left;
  }
`;

const StepId = styled.div`
  border-radius: 16px;
  width: 32px;
  height: 32px;
  background: #dde6ed;
  font-size: 20px;
  line-height: 32px;
  text-align: center;
  display: inline-block;
`;

const StepTitle = styled(H3)`
  font-size: 20px;
  line-height: 120%;
  margin-bottom: 8px;
  @media (max-width: ${breakpoints.tablet}) {
    font-size: 18px;
    margin-bottom: 4px;
  }
`;

const StepDescription = styled(P1)`
  font-size: 16px;
  line-height: 150%;
  font-weight: 400;
  margin-bottom: 0px;
  @media (max-width: ${breakpoints.tablet}) {
    font-size: 14px;
  }
`;

const Cover = styled.div`
  margin-top: 80px;
  grid-column: span 24;
  position: relative;
  @media (max-width: ${breakpoints.tablet}) {
    grid-column: 13/26;
    margin-top: 0;
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 40px;
  }
`;

const StepContainer = styled.div`
  margin-bottom: ${({ $last }) => ($last ? '0px' : '40px')};
  grid-column: ${({ $gridColumn }) => $gridColumn};
  text-align: ${({ $textAlign }) => ($textAlign ? 'right' : 'left')};
  @media (max-width: ${breakpoints.tablet}) {
    grid-column: span 24;
    text-align: left;
    display: grid;
    grid-template-columns: auto 1fr;
    margin-bottom: ${({ $last }) => ($last ? '0px' : '24px')};
  }
  @media (max-width: ${breakpoints.mobile}) {
    text-align: left;
    display: grid;
    grid-column: span 24;
    grid-template-columns: auto 1fr;
    margin-bottom: 24px;
  }
`;

const BgImage = styled.div`
  background-image: url(/static/images/k12/curve.png);
  height: 100%;
  background-repeat: no-repeat;
  position: absolute;
  width: 146px;
  background-size: contain;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  z-index: -1;
  @media (max-width: ${breakpoints.tablet}) {
    background-image: url(/static/images/k12/curve-mobile.png);
    background-repeat: repeat;
    width: 30.3px;
    height: 100%;
    margin: 0;
  }
  @media (max-width: ${breakpoints.mobile}) {
    width: 30.3px;
    height: 260px;
    background-image: url(/static/images/k12/curve-mobile.png);
  }
  @media (max-width: 400px) {
    height: 320px;
  }
`;

const SubscriptionContainer = styled(Container)`
  margin-top: 160px;
  @media (max-width: ${breakpoints.tablet}) {
    margin-top: 80px;
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 64px;
  }
`;

const StepContent = styled.div`
  margin-left: 16px;
`;

const FeaturedContainer = styled(Container)`
  @media (max-width: ${breakpoints.tablet}) {
    align-items: flex-start;
  }
`;

const Step = ({ data: { title, subtitle, description }, index, $last }) => (
  <StepContainer
    $textAlign={index % 2 === 0}
    $gridColumn={index % 2 === 0 ? '4/12' : '14/21'}
    $last={$last}
  >
    <StepId>{title}</StepId>
    <StepContent>
      <StepTitle>{subtitle}</StepTitle>
      <StepDescription>{description}</StepDescription>
    </StepContent>
  </StepContainer>
);

function Features({ children, data }) {
  const { headerTitle, leftImage, rightImage, steps } = data;
  const isMobile = useSelector(({ helper }) => helper.isMobile);
  return (
    <BackgroundColor>
      <FeaturedContainer>
        <FullWidth>
          <Block>
            <Image src={leftImage} width="80" height="80" loading="lazy" />
            <Icon
              src={imageHelper.getImage('k12/plus-circle.png')}
              width="24"
              height="24"
              alt="plus"
            />
            <Image src={rightImage} width="80" height="80" loading="lazy" />
          </Block>
          <Header>{headerTitle}</Header>
        </FullWidth>
        <Cover>
          <BgImage $isMobile={isMobile} />
          {steps.map((_data, index) => (
            <StepCover key={_data.id}>
              <Step
                data={_data}
                index={index}
                $last={index === steps.length - 1}
              />
            </StepCover>
          ))}
        </Cover>
      </FeaturedContainer>
      <SubscriptionContainer>{children}</SubscriptionContainer>
    </BackgroundColor>
  );
}

Step.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  index: PropTypes.number.isRequired,
  $last: PropTypes.bool.isRequired
};

Features.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]).isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired
};

export default Features;
